import type { DecisionConsoleReadModelV1 } from './decision-console';
import {
  buildDriftMonitoringExperience,
  type DriftInterventionRowV1,
} from './drift-monitoring-experience';

export const INTERVENTION_QUEUE_ACTION_VERBS = [
  'Approve',
  'Modify',
  'Defer',
  'Escalate',
] as const;

export type InterventionQueueActionVerb =
  (typeof INTERVENTION_QUEUE_ACTION_VERBS)[number];

export type InterventionQueueActionIntent = Readonly<{
  verb: InterventionQueueActionVerb;
  /**
   * This lane admits display intents only. A future binding requires a shared
   * verb-to-ledger action contract; producers must never infer one from an ID.
   */
  actionId: null;
}>;

export type InterventionQueueRootCause = Readonly<{
  text: string;
  evidenceState: 'evidence_linked' | 'gap';
}>;

export type InterventionQueueTableRow = Readonly<{
  id: string;
  driftSignal: string;
  rootCause: InterventionQueueRootCause;
  owner: string | null;
  recommendedIntervention: string;
  decisionRequiredBy: readonly string[];
  actionIntents: readonly InterventionQueueActionIntent[];
}>;

export type InterventionQueueRiskPosture =
  | 'stable'
  | 'watch'
  | 'intervention_required'
  | 'evidence_gap';

export type InterventionQueueTablePayload = Readonly<{
  title: string;
  riskPosture: InterventionQueueRiskPosture;
  rows: readonly InterventionQueueTableRow[];
}>;

export type InterventionQueueTableAdapterOptions = Readonly<{
  /**
   * Optional governed bet scope. Omit only when the caller intends to render
   * the complete Decision Console queue.
   */
  betKeys?: readonly string[];
}>;

const OWNER_PLACEHOLDERS = new Set(['', 'owner to assign', 'unassigned', 'tbd', '-']);
const CAUSE_EVIDENCE_GAP =
  'Cause evidence gap — no governed causal watch is bound to this intervention.';

export function interventionQueueTablePayloadFromDecisionConsole(
  model: DecisionConsoleReadModelV1,
  options: InterventionQueueTableAdapterOptions = {},
): InterventionQueueTablePayload {
  const scopedModel = scopeDecisionConsoleModel(model, options.betKeys);
  const queue = buildDriftMonitoringExperience({
    readModel: scopedModel,
    watchEvents: scopedModel.watchEvents ?? [],
  }).interventionQueue;

  return {
    title: 'Weekly intervention queue',
    riskPosture: riskPostureFromDecisionConsole(scopedModel),
    rows: queue.rows.map((row) =>
      interventionQueueTableRowFromDrift(row, scopedModel),
    ),
  };
}

function interventionQueueTableRowFromDrift(
  row: DriftInterventionRowV1,
  model: DecisionConsoleReadModelV1,
): InterventionQueueTableRow {
  const owner = governedOwner(row.ownerLabel, row.ownerAssigned);
  const recommendedIntervention = row.priceLabel
    ? `${row.recommendedIntervention} — ${row.priceLabel}`
    : row.recommendedIntervention;

  return {
    id: row.id,
    driftSignal: [row.driftSignal, row.driftMagnitude].filter(Boolean).join(' · '),
    rootCause: rootCauseFor(row, model),
    owner,
    recommendedIntervention,
    decisionRequiredBy: owner
      ? row.decisionRequiredBy.filter((decisionOwner) => governedOwner(decisionOwner, true))
      : [],
    actionIntents: INTERVENTION_QUEUE_ACTION_VERBS.map((verb) => ({
      verb,
      actionId: null,
    })),
  };
}

function rootCauseFor(
  row: DriftInterventionRowV1,
  model: DecisionConsoleReadModelV1,
): InterventionQueueRootCause {
  if (row.origin === 'watch_event') {
    return { text: row.rootCause, evidenceState: 'evidence_linked' };
  }
  const betKey = row.id.startsWith('bet:') ? row.id.slice('bet:'.length) : '';
  const bet = model.bets.find((candidate) => candidate.key === betKey);
  if (!bet) {
    return { text: CAUSE_EVIDENCE_GAP, evidenceState: 'gap' };
  }
  const causalWatch = (model.watchEvents ?? []).find(
    (event) =>
      event.affectedIdentity.kind === 'commitment' &&
      (event.affectedIdentity.id === bet.commitmentId ||
        event.affectedIdentity.id === bet.decisionId),
  );
  return causalWatch
    ? {
        text: `${causalWatch.watchLabel} fired; ${causalWatch.suggestedQuestion}`,
        evidenceState: 'evidence_linked',
      }
    : { text: CAUSE_EVIDENCE_GAP, evidenceState: 'gap' };
}

function scopeDecisionConsoleModel(
  model: DecisionConsoleReadModelV1,
  betKeys: readonly string[] | undefined,
): DecisionConsoleReadModelV1 {
  if (!betKeys) {
    return model;
  }
  const includedBetKeys = new Set(betKeys);
  const bets = model.bets.filter((bet) => includedBetKeys.has(bet.key));
  const includedIdentityIds = new Set(
    bets.flatMap((bet) => [bet.key, bet.commitmentId, bet.decisionId]),
  );
  const watchEvents = (model.watchEvents ?? []).filter((event) => {
    if (event.affectedIdentity.kind === 'commitment') {
      return includedIdentityIds.has(event.affectedIdentity.id);
    }
    return Boolean(
      event.affectedIdentity.scopeId &&
        includedIdentityIds.has(event.affectedIdentity.scopeId),
    );
  });

  return {
    ...model,
    bets,
    decisionDocket: model.decisionDocket.filter((item) =>
      includedBetKeys.has(item.linkedBetKey),
    ),
    watchEvents,
    riskRadar: riskRadarForBets(model, bets),
  };
}

function riskRadarForBets(
  model: DecisionConsoleReadModelV1,
  bets: readonly DecisionConsoleReadModelV1['bets'][number][],
): DecisionConsoleReadModelV1['riskRadar'] {
  const count = (status: DecisionConsoleReadModelV1['bets'][number]['drift']['status']) =>
    bets.filter((bet) => bet.drift.status === status).length;
  const atRiskBets = bets.filter(
    (bet) =>
      bet.drift.status === 'drifting' ||
      bet.drift.status === 'amendment_required',
  );
  const pricedMisses = atRiskBets.map((bet) => bet.drift.projectedMissUsd);
  const valueAtRiskUsd =
    pricedMisses.length > 0 && pricedMisses.every((value) => value !== null)
      ? pricedMisses.reduce<number>((total, value) => total + (value ?? 0), 0)
      : null;

  return {
    tracked: bets.length,
    onTrack: count('on_track'),
    watch: count('watch'),
    drifting: count('drifting'),
    amendmentRequired: count('amendment_required'),
    gap: count('gap'),
    valueAtRiskUsd,
    valueAtRiskBasis:
      valueAtRiskUsd === null ? null : model.riskRadar.valueAtRiskBasis,
    valueAtRiskLabel:
      valueAtRiskUsd === null ? null : model.riskRadar.valueAtRiskLabel,
  };
}

function riskPostureFromDecisionConsole(
  model: DecisionConsoleReadModelV1,
): InterventionQueueRiskPosture {
  if (model.riskRadar.gap > 0 || model.riskRadar.tracked === 0) {
    return 'evidence_gap';
  }
  if (model.riskRadar.amendmentRequired > 0 || model.riskRadar.drifting > 0) {
    return 'intervention_required';
  }
  if (model.riskRadar.watch > 0 || (model.watchEvents?.length ?? 0) > 0) {
    return 'watch';
  }
  return 'stable';
}

function governedOwner(rawOwner: string | null | undefined, assigned: boolean): string | null {
  const owner = rawOwner?.trim() ?? '';
  if (!assigned || OWNER_PLACEHOLDERS.has(owner.toLowerCase())) {
    return null;
  }
  return owner;
}
